import React, {
  useEffect,
  createContext,
  useState,
  useCallback,
  useContext,
} from 'react'
import {useLazyQuery, QueryLazyOptions} from '@apollo/client'
import {fetcher} from '../api/rest'
import {meQuery} from './user-queries'
import {Me_me, Me} from '../components/Login/_gql-types/Me'
import useSWR from 'swr'

export interface ExtendedUser extends Me_me {
  displayName: string
  name: string
}

export interface CompanyAddress {
  id: Number
  name: string
  street_address_1: string
  street_address_2: string
  city: string
  city_area: string
  postal_code: string
  phone: string
}

export interface CompanyMember {
  id: Number
  company: Number
  user: Number
  role: 'admin' | 'staff'
}

export interface Company {
  id: Number
  name: string
  about: string
  members: CompanyMember[]
  addresses: CompanyAddress[]
  default_shipping_address: Number
  default_billing_address: Number
}

interface UserContextProps {
  user?: ExtendedUser
  userLoading?: boolean
  userCompanies?: Company[]
  userCompaniesLoading?: boolean
  authToken?: string
  fetchUser: (options?: QueryLazyOptions<Record<string, any>> | undefined) => void
  setAuthToken: (token?: string) => void
  setCompanyId: (companyId?: string) => void
  company?: Company
}

export const UserContext = createContext<Partial<UserContextProps>>({})

interface Props {
  children: React.ReactNode
}

export function UserProvider({children}: Props) {
  const [authToken, setAuthTokenState] = useState<string | undefined>(
    localStorage.getItem('jwt') || undefined
  )
  const [companyId, setCompanyIdState] = useState<string | undefined>(
    localStorage.getItem('companyId') || undefined
  )

  const {
    data: userCompanies,
    isValidating: userCompaniesLoading,
  } = useSWR<Company[]>(
    authToken ? ['/companies/?me=true', authToken] : null,
    fetcher.get,
    {revalidateOnFocus: false},
  )

  const company = (companyId != null
    ? userCompanies?.find((c) => `${c.id}` === companyId)
    : undefined) || userCompanies?.[0]

  const [fetchUser, {data, loading}] = useLazyQuery<Me>(meQuery, {fetchPolicy: 'no-cache'})

  const setAuthToken = useCallback((token?: string) => {
    if (token) {
      localStorage.setItem('jwt', token)
    } else {
      localStorage.removeItem('jwt')
    }
    setAuthTokenState(token)
  }, [setAuthTokenState])

  const setCompanyId = useCallback((companyId?: string) => {
    if (companyId) {
      localStorage.setItem('companyId', companyId)
    } else {
      localStorage.removeItem('companyId')
    }
    setCompanyIdState(`${companyId}`)
  }, [setCompanyIdState])

  useEffect(() => {
    fetchUser()
  }, [fetchUser, authToken])

  const user = data?.me
  const userName = user ? `${user.firstName} ${user.lastName}`.trim() : ''

  return (
    <UserContext.Provider
      value={{
        user: user ? {
          ...user,
          displayName: userName || user.email,
          name: userName,
        } : undefined,
        userLoading: loading,
        userCompanies,
        userCompaniesLoading,
        fetchUser,
        authToken,
        setAuthToken,
        setCompanyId,
        company,
      }}
    >
      {children}
    </UserContext.Provider>
  )
}

export function useAuth() {
  return useContext(UserContext) as UserContextProps
}
